import React, {useEffect, useMemo, useState} from 'react';
import {ShieldCheck, RefreshCw, Save, Search, AlertTriangle} from 'lucide-react';

const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
async function request(path, options={}) {
  const response = await fetch(`${base}/api/kp/admin/pilot${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json', ...(options.headers || {}) },
    cache: 'no-store'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.msg || `HTTP ${response.status}`);
  return data;
}

export default function KpPilotAdmin() {
  const [data,setData] = useState(null);
  const [selected,setSelected] = useState([]);
  const [enabled,setEnabled] = useState(false);
  const [search,setSearch] = useState('');
  const [choices,setChoices] = useState([]);
  const [error,setError] = useState('');
  const [info,setInfo] = useState('');
  const [loading,setLoading] = useState(false);
  const limit = Number(data?.limit || 100);
  const users = useMemo(() => {
    const unique = new Map();
    [...(data?.users || []), ...choices].forEach(u => unique.set(String(u.id), u));
    return Array.from(unique.values());
  }, [data,choices]);

  async function load() {
    setError('');
    try {
      const d = await request('');
      setData(d);
      setSelected(d.users.map(x => Number(x.id)));
      setEnabled(Boolean(d.enabled));
      setChoices([]);
    } catch(e) {setError(e.message);}
  }
  useEffect(() => {load();}, []);
  async function find() {
    setError('');setInfo('');
    if (search.trim().length < 3) {setError('Masukkan setidaknya 3 karakter nama, NIP, atau ID.');return;}
    try {
      const matches = await request(`/candidates?q=${encodeURIComponent(search.trim())}`);
      setChoices(old => {
        const m = new Map(old.map(x => [String(x.id), x]));
        matches.forEach(x => m.set(String(x.id), x));
        return Array.from(m.values());
      });
      if (!matches.length) setInfo('Tidak ditemukan akun guru dengan profil untuk pencarian tersebut.');
    } catch(e) {setError(e.message);}
  }
  function toggle(id) {
    setError('');setInfo('');
    setSelected(old => {
      if (old.includes(id)) return old.filter(x => x !== id);
      if (old.length >= limit) {setError(`Maksimal ${limit} akun guru dalam pilot.`);return old;}
      return [...old,id];
    });
  }
  async function save() {
    const msg = `Simpan akses KP terbatas untuk ${selected.length} akun guru?\n\n` +
      `Mode pilot: ${enabled?'AKTIF':'NONAKTIF'}.\n` +
      'Ini adalah SIPAK produksi. Akun yang diizinkan dapat membuat atau mengirim usulan sungguhan sesuai aturan periode.\n' +
      'Akun lain tetap terkunci. Lanjutkan?';
    if (!window.confirm(msg)) return;
    setLoading(true);setError('');setInfo('');
    try {
      const d = await request('', {method:'PUT',body:JSON.stringify({enabled,ids:selected})});
      setData(d);setSelected(d.users.map(x => Number(x.id)));
      setInfo(`Pengaturan berhasil disimpan (${d.users.length} akun). Guru lain tetap terkunci.`);
    } catch(e) {setError(e.message);} finally {setLoading(false);}
  }
  return <div className="p-4 md:p-7 max-w-4xl mx-auto space-y-5 text-slate-800">
    <div className="flex items-center gap-3">
      <ShieldCheck className="text-blue-700" size={32}/>
      <div><h2 className="text-2xl font-bold">Akses KP Terbatas</h2>
        <p className="text-slate-600">Pilih akun guru yang diizinkan mengakses pengusulan Kenaikan Pangkat.</p></div>
    </div>
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
      <AlertTriangle className="shrink-0 text-amber-700"/>
      <p>Guru QA dan guru asli dapat dipilih. <strong>Usulan guru asli tersimpan di database dan Drive SIPAK produksi</strong>, bukan sandbox. Gunakan hanya untuk uji coba resmi yang disetujui. Daftar Maintenance terpisah dari daftar akses KP.</p>
    </div>
    <section className="bg-white border rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div><strong>Aktifkan akses KP terbatas</strong><p className="text-sm text-slate-500">Hanya akun di daftar yang dapat menggunakan KP.</p></div>
        <input aria-label="Mode akses KP terbatas" type="checkbox" checked={enabled} onChange={e=>setEnabled(e.target.checked)} className="w-5 h-5"/>
      </div>
      <div className="flex gap-2">
        <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();find();}}} placeholder="Cari nama, NIP, atau ID akun guru" aria-label="Cari guru" className="flex-1 border rounded-lg p-3 min-w-0"/>
        <button type="button" onClick={find} className="px-4 rounded-lg border flex items-center gap-2"><Search size={16}/>Cari</button>
      </div>
      <p className="font-semibold">Dipilih: {selected.length}/{limit} akun</p>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {users.map(u=><label key={u.id} className="flex items-start gap-3 border rounded-lg p-3">
          <input type="checkbox" checked={selected.includes(Number(u.id))} onChange={()=>toggle(Number(u.id))} className="w-5 h-5 mt-1"/>
          <div className="min-w-0"><strong>{u.full_name}</strong>
            <p className="text-sm text-slate-500">NIP: {u.username} · ID akun: {u.id}</p></div>
        </label>)}
      </div>
      {!users.length && <p className="text-sm text-slate-500">Cari guru lalu centang untuk menambahkan. Akun QA lama yang tersimpan akan tetap ditampilkan.</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={save} disabled={loading||!data} className="bg-blue-700 text-white font-bold px-4 py-3 rounded-lg flex items-center gap-2 disabled:opacity-50"><Save size={16}/>{loading?'Menyimpan...':'Simpan Pengaturan'}</button>
        <button type="button" onClick={load} className="border rounded-lg px-4 py-3 flex items-center gap-2"><RefreshCw size={16}/>Muat Ulang</button>
      </div>
      {error && <p role="alert" className="text-red-700 font-semibold">{error}</p>}
      {info && <p role="status" className="text-green-700 font-semibold">{info}</p>}
    </section>
  </div>;
}
